import React, { useState, useEffect } from 'react';
import { startScan, getScan, cancelScan } from '../services/data/trustTechScanService';
import { useNavigate } from 'react-router-dom';

export default function RunTrustTechScan(){
  const [step, setStep] = useState(0); // 0 config, 1 review, 2 running
  const [scanId, setScanId] = useState(null);
  const [scan, setScan] = useState(null);
  const [form, setForm] = useState({
    scope:'all-suppliers',
    timeframe:'30d',
    includeESG:true,
    includeRisk:true,
    includeOptimization:true
  });
  const navigate = useNavigate();

  useEffect(()=>{
    let t;
    if(scanId){
      const poll = async ()=>{
        const s = await getScan(scanId);
        setScan(s ? { ...s } : null);
        if(s && s.status === 'running'){
          t = setTimeout(poll, 1600);
        }
      };
      poll();
    }
    return ()=> clearTimeout(t);
  }, [scanId]);

  const launch = async ()=>{
    const { id } = await startScan(form);
    setScanId(id);
    setStep(2);
  };

  const reset = ()=>{
    setStep(0);
    setScanId(null);
    setScan(null);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Run Trust Tech Scan</h1>
        <p className="text-slate-600 text-sm mt-1">Configure, launch and monitor a new scan across your supplier ecosystem.</p>
      </header>

      {step === 0 && <ConfigStep form={form} setForm={setForm} onNext={()=> setStep(1)} onCancel={()=> navigate(-1)} />}
      {step === 1 && <ReviewStep form={form} onBack={()=> setStep(0)} onLaunch={launch} />}
      {step === 2 && <ProgressStep scan={scan} onCancel={()=> cancelScan(scanId).then(setScan)} onReset={reset} scanId={scanId} />}
    </div>
  );
}

function Section({title, children}){
  return (
    <section className="bg-white border rounded-xl p-6 space-y-6 shadow-sm">
      {title && <h2 className="font-medium text-lg">{title}</h2>}
      {children}
    </section>
  );
}

function ConfigStep({form, setForm, onNext, onCancel}){
  return (
    <Section>
      <div>
        <label className="font-medium text-sm">Scope</label>
        <select className="mt-1 w-full border rounded-md px-3 py-2 text-sm" value={form.scope} onChange={e=> setForm(f=>({...f, scope:e.target.value}))}>
          <option value="all-suppliers">All Active Suppliers</option>
          <option value="tier-1">Tier 1 Suppliers</option>
          <option value="tier-2">Tier 2 Suppliers</option>
          <option value="custom">Custom Selection (future)</option>
        </select>
      </div>
      <div>
        <label className="font-medium text-sm">Timeframe</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {['30d','60d','90d','180d'].map(tf=>(
            <button key={tf} onClick={()=> setForm(f=>({...f, timeframe:tf}))} className={`px-3 py-1.5 rounded-md text-sm border ${form.timeframe===tf ? 'bg-purple-600 text-white border-purple-600':'bg-white hover:bg-slate-50 border-slate-300'}`}>{tf}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="font-medium text-sm">Signal Modules</label>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {[
            {key:'includeESG', label:'ESG Signals'},
            {key:'includeRisk', label:'Risk Radar'},
            {key:'includeOptimization', label:'Optimization Opportunities'}
          ].map(m=>(
            <label key={m.key} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form[m.key]} onChange={e=> setForm(f=>({...f, [m.key]: e.target.checked}))} />
              {m.label}
            </label>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-md border text-sm bg-white hover:bg-slate-50">Cancel</button>
        <button onClick={onNext} className="px-4 py-2 rounded-md text-sm bg-purple-600 text-white hover:bg-purple-500">Next</button>
      </div>
    </Section>
  );
}

function ReviewStep({form, onBack, onLaunch}){
  return (
    <Section title="Review & Launch">
      <div className="text-sm space-y-1">
        <Row label="Scope" value={form.scope} />
        <Row label="Timeframe" value={form.timeframe} />
        <Row label="Modules" value={['includeESG','includeRisk','includeOptimization'].filter(k=>form[k]).length} />
      </div>
      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="px-4 py-2 rounded-md border text-sm bg-white hover:bg-slate-50">Back</button>
        <button onClick={onLaunch} className="px-4 py-2 rounded-md text-sm bg-green-600 text-white hover:bg-green-500">Launch Scan</button>
      </div>
    </Section>
  );
}

function Row({label, value}){ return <div><span className="font-semibold mr-1">{label}:</span>{value}</div>; }

function ProgressStep({scan, onCancel, onReset, scanId}){
  return (
    <Section title="Scan Progress">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">ID: {scanId}</div>
        {scan?.status === 'running' && <button onClick={onCancel} className="text-xs px-2 py-1 rounded border border-rose-300 text-rose-600 hover:bg-rose-50">Cancel</button>}
      </div>
      <div>
        <div className="h-3 w-full bg-slate-200 rounded overflow-hidden">
          <div className="h-full bg-purple-600 transition-all" style={{width:`${scan?.progress||0}%`}} />
        </div>
        <div className="mt-2 text-xs text-slate-600">
          {scan?.status === 'completed' && 'Completed'}
          {scan?.status === 'running' && `Running… ${Math.floor(scan?.progress||0)}%`}
          {scan?.status === 'cancelled' && 'Cancelled'}
          {!scan && 'Starting…'}
        </div>
      </div>
      {scan?.status === 'completed' && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm grid gap-2">
          <div className="font-medium">Results</div>
          <div className="grid grid-cols-3 gap-4">
            <Metric label="Suppliers Analyzed" value={scan.result.suppliersAnalyzed} />
            <Metric label="Issues Found" value={scan.result.issuesFound} />
            <Metric label="Optimization Ops" value={scan.result.optimizationOps} />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onReset} className="px-3 py-2 rounded-md border text-xs bg-white hover:bg-slate-50">Run Another</button>
          </div>
        </div>
      )}
    </Section>
  );
}

function Metric({label, value}){
  return (
    <div className="p-3 bg-white rounded-md border text-center">
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
